import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { log } from "@/lib/log";
import { toast } from "sonner";
import { Bell, MapPin, CalendarClock, Clock, X, Settings, ShieldCheck } from "lucide-react";

const POLL_MS = 15_000;
const ALERT_KINDS = [
  { key: "geofence", label: "Geofence violations" },
  { key: "time_off", label: "New time-off requests" },
  { key: "missed", label: "Missed clock-ins" },
];

/**
 * Admin bell icon with live alert counts + dropdown panel.
 * Silently no-ops for non-admin users.
 */
export default function AlertsBell({ role }) {
  const [data, setData] = useState({ totals: { grand: 0 }, geofence_violations: [], time_off_pending: [], missed_clock_ins: [], probation_cleared: [], prefs: { geofence: true, time_off: true, missed: true, probation: true } });
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts");
      setData(data);
    } catch (err) {
      log.warn("Alerts fetch failed:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    if (role !== "admin") return;
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, [role, load]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setPrefsOpen(false); } };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const togglePref = async (key) => {
    const next = { ...data.prefs, [key]: !data.prefs[key] };
    setData({ ...data, prefs: next });
    try {
      await api.put("/alerts/prefs", next);
      load();  // re-fetch so counts reflect the new filter immediately
    } catch (err) {
      toast.error("Could not save alert preferences");
      setData({ ...data, prefs: data.prefs });  // rollback
    }
  };

  if (role !== "admin") return null;

  const total = data.totals?.grand ?? 0;
  const hasAny = total > 0;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        data-testid="alerts-bell"
        onClick={() => setOpen((o) => !o)}
        aria-label="Admin alerts"
        className={`relative w-10 h-10 border-2 border-ink flex items-center justify-center transition ${hasAny ? "bg-warn text-ink" : "bg-white text-ink"} hover:bg-ink hover:text-white`}
      >
        <Bell className="w-4 h-4" />
        {hasAny && (
          <span data-testid="alerts-count" className="absolute -top-2 -right-2 bg-bad text-white text-[10px] font-mono font-black rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center border-2 border-white">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="alerts-panel" className="absolute right-0 top-12 w-96 bg-white border-2 border-ink shadow-lg z-40 max-h-[70vh] overflow-auto">
          <div className="sticky top-0 bg-ink text-white px-4 py-3 flex items-center justify-between">
            <div className="overline">§ ADMIN ALERTS · {total}</div>
            <div className="flex items-center gap-2">
              <button data-testid="alerts-prefs-toggle" onClick={() => setPrefsOpen((o) => !o)} aria-label="Preferences" className="hover:text-warn" title="Alert preferences">
                <Settings className={`w-4 h-4 ${prefsOpen ? "text-warn" : ""}`} />
              </button>
              <button onClick={() => setOpen(false)} aria-label="Close" className="hover:text-warn"><X className="w-4 h-4" /></button>
            </div>
          </div>

          {prefsOpen && (
            <div data-testid="alerts-prefs-panel" className="bg-secondary/60 border-b-2 border-ink px-4 py-3 space-y-2">
              <div className="overline text-[10px] mb-1">§ SHOW ME</div>
              {ALERT_KINDS.map((k) => (
                <label key={k.key} className="flex items-center gap-2 cursor-pointer font-mono text-xs">
                  <input
                    data-testid={`pref-${k.key}`}
                    type="checkbox"
                    checked={!!data.prefs?.[k.key]}
                    onChange={() => togglePref(k.key)}
                    className="w-4 h-4 accent-ink"
                  />
                  <span>{k.label}</span>
                </label>
              ))}
              <div className="pt-3 mt-3 border-t border-ink/20 space-y-2" data-testid="sms-escalation-block">
                <div className="overline text-[10px]">§ SMS ESCALATION</div>
                <label className="flex items-center gap-2 cursor-pointer font-mono text-xs">
                  <input
                    data-testid="pref-sms-escalation"
                    type="checkbox"
                    checked={!!data.prefs?.sms_escalation}
                    onChange={() => togglePref("sms_escalation")}
                    className="w-4 h-4 accent-ink"
                  />
                  <span>Text me when a geofence alert sits unhandled 1+ hour</span>
                </label>
                <input
                  data-testid="pref-sms-phone"
                  type="tel"
                  placeholder="+15551234567"
                  value={data.prefs?.sms_phone || ""}
                  onChange={(e) => setData({ ...data, prefs: { ...data.prefs, sms_phone: e.target.value } })}
                  onBlur={async () => {
                    try {
                      await api.put("/alerts/prefs", data.prefs);
                      toast.success("SMS phone saved");
                    } catch { toast.error("Could not save phone"); }
                  }}
                  className="w-full px-2 py-1.5 border-2 border-ink font-mono text-xs disabled:opacity-40"
                  disabled={!data.prefs?.sms_escalation}
                />
              </div>
              <div className="font-mono text-[9px] text-muted-foreground pt-1">Preferences are saved per admin account — the bell only nags you about flows you own.</div>
            </div>
          )}

          {data.prefs?.geofence && (
            <Section
              testid="geofence-section"
              title="Geofence Violations"
              icon={MapPin}
              items={data.geofence_violations}
              emptyLabel="No off-site clock-ins pending review"
              renderItem={(it) => (
                <div>
                  <div className="font-semibold text-sm">{it.employee_name || "Employee"}</div>
                  <div className="text-xs text-muted-foreground truncate">{it.flag_reason || "Outside worksite radius"}{it.worksite_name ? ` · ${it.worksite_name}` : ""}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{it.clock_in_at ? new Date(it.clock_in_at).toLocaleString() : ""}</div>
                </div>
              )}
              linkTo="/app/approvals"
              linkLabel="Review in Approvals →"
            />
          )}

          {data.prefs?.time_off && (
            <Section
              testid="time-off-section"
              title="New Time-Off Requests"
              icon={CalendarClock}
              items={data.time_off_pending}
              emptyLabel="No pending time-off requests"
              renderItem={(it) => (
                <div>
                  <div className="font-semibold text-sm">{it.employee_name || "Employee"} · <span className="uppercase text-xs">{it.type}</span></div>
                  <div className="text-xs text-muted-foreground">{it.start_date} → {it.end_date} · {Number(it.hours || 0).toFixed(2)}h</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{it.created_at ? new Date(it.created_at).toLocaleString() : ""}</div>
                </div>
              )}
              linkTo="/app/approvals"
              linkLabel="Approve/Reject →"
            />
          )}

          {data.prefs?.missed && (
            <Section
              testid="missed-section"
              title="Missed Clock-Ins (24h)"
              icon={Clock}
              items={data.missed_clock_ins}
              emptyLabel="Everyone with a worksite has clocked in"
              renderItem={(it) => (
                <div>
                  <div className="font-semibold text-sm">{it.employee_name}</div>
                  <div className="text-xs text-muted-foreground">{it.email}</div>
                </div>
              )}
              linkTo="/app/employees"
              linkLabel="Contact employees →"
            />
          )}

          {data.prefs?.probation && (
            <Section
              testid="probation-section"
              title="Probation Cleared · HR Docs Unlocked"
              icon={ShieldCheck}
              items={data.probation_cleared}
              emptyLabel="No one hit their 90-day mark this week"
              renderItem={(it) => (
                <div>
                  <div className="font-semibold text-sm">{it.employee_name}</div>
                  <div className="text-xs text-muted-foreground">Hired {it.hire_date} · cleared {it.cleared_on}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{it.days_since_hire} days in — HR documents now visible to the employee</div>
                </div>
              )}
              linkTo="/app/employees"
              linkLabel="Review employee →"
            />
          )}

          {!(data.prefs?.geofence || data.prefs?.time_off || data.prefs?.missed || data.prefs?.probation) && (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground italic" data-testid="all-muted-note">All alert types are muted — open the ⚙ preferences above to enable one.</div>
          )}

          <div className="px-4 py-2 text-[10px] font-mono text-muted-foreground border-t border-ink/20">
            Live · refreshes every 15s · {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : ""}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ testid, title, icon: Icon, items, emptyLabel, renderItem, linkTo, linkLabel }) {
  return (
    <div className="border-b-2 border-ink" data-testid={testid}>
      <div className="px-4 py-2 bg-secondary/60 flex items-center justify-between">
        <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5" /><span className="overline">{title}</span></div>
        <span className="font-mono text-xs font-black">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-3 text-xs text-muted-foreground italic">{emptyLabel}</div>
      ) : (
        <ul className="divide-y divide-ink/10">
          {items.slice(0, 5).map((it, idx) => (
            <li key={it.id || `${testid}-${idx}`} className="px-4 py-3" data-testid={`${testid}-item-${idx}`}>
              {renderItem(it)}
            </li>
          ))}
        </ul>
      )}
      <Link to={linkTo} className="block px-4 py-2 text-xs font-mono uppercase tracking-widest text-ink hover:bg-secondary">{linkLabel}</Link>
    </div>
  );
}
